import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-document-viewer-dialog',
  templateUrl: './document-viewer-dialog.component.html',
  styleUrls: ['./document-viewer-dialog.component.scss']
})
export class DocumentViewerDialogComponent implements OnInit, OnDestroy {
  @ViewChild('pdfContainer') pdfContainer?: ElementRef;

  fileUrl: SafeResourceUrl | null = null;
  fileName: string = 'Document';
  fileType: string = '';
  fileExtension: string = '';
  isLoading: boolean = true;
  isLoadingDownloadDocument:boolean = false;
  error: string = '';
  isPdf: boolean = false;
  isImage: boolean = false;
  isOfficeDocument: boolean = false;
  pdfLoading: boolean = false;
  downloadError: boolean = false;
  documentLink: string = '';

  private destroy$ = new Subject<void>();

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    const document = this.config.data?.document;
    
    if (!document) {
      this.error = 'Document information is missing';
      this.isLoading = false;
      return;
    }

    this.fileName = document.nom_fichier || 'Document';
    this.documentLink = document.file_link;

    // Extract file extension from filename
    this.fileExtension = this.getFileExtension(this.fileName);

    if (!this.documentLink) {
      this.error = 'Le fichier est introuvable ou inaccessible';
      this.isLoading = false;
      return;
    }

    this.loadDocument();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  private async checkDocumentAvailability(): Promise<boolean> {
    try {
      const response = await fetch(this.documentLink);
      
      if (response.ok) {
        // Vérifier si la réponse est du JSON (cas du lien expiré)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const jsonResponse = await response.json();
          if (jsonResponse.success === false && jsonResponse.message === "Lien expiré") {
            this.error = 'Le lien du document a expiré. Veuillez recharger pour obtenir un nouveau lien.';
            return false;
          }
        }
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  private async loadDocument(): Promise<void> {
    try {
      // Vérifier d'abord si le document est accessible
      const isAvailable = await this.checkDocumentAvailability();
      
      if (!isAvailable) {
        this.error = 'Le document demandé est introuvable ou inaccessible.';
        this.isLoading = false;
        return;
      }

      // Determine document type based on file extension
      this.isPdf = this.fileExtension === 'pdf';
      this.isImage = ['jpg', 'jpeg', 'png'].includes(this.fileExtension);
      this.isOfficeDocument = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(this.fileExtension);
      
      // Use direct URL
      const pdfViewerParams = this.isPdf ? '#toolbar=0&navpanes=0&scrollbar=0' : '';
      this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.documentLink + pdfViewerParams);
      
      // Special handling for PDFs
      if (this.isPdf) {
        this.pdfLoading = true;
      }
      
      this.isLoading = false;
    } catch (error) {
      console.error('Error processing document:', error);
      this.error = 'Erreur lors du traitement du document';
      this.isLoading = false;
    }
  }

  // Called when PDF iframe loads successfully
  onPdfLoad(): void {
    this.pdfLoading = false;
  }

  // Called when PDF iframe fails to load
  onPdfError(): void {
    this.pdfLoading = false;
    this.error = 'Le document ne peut pas être chargé. Le fichier est introuvable ou inaccessible.';
  }

  onImageError(): void {
    this.error = 'L\'image ne peut pas être chargée. Le fichier est introuvable ou corrompu.';
  }

  closeDialog(): void {
    this.ref.close();
  }

  downloadDocument(): void {
  if (!this.documentLink) {
    this.downloadError = true;
    return;
  }

  this.isLoadingDownloadDocument = true;
  
  // Fetch the file as a blob first
  fetch(this.documentLink)
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.blob();
    })
    .then(blob => {
      // Create a blob URL from the fetched file
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = this.fileName;
      
      // Append to body, click and clean up
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        this.isLoadingDownloadDocument = false;
      }, 100);
    })
    .catch(error => {
      console.error('Download failed:', error);
      this.downloadError = true;
      this.isLoadingDownloadDocument = false;
    });
}
}